import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  Paper,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Drawer,
  Divider,
  Switch,
  FormControlLabel,
  useTheme,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blacklistService } from '../services/blacklistService';
import { BlacklistEntry } from '../types/api';
import { format, formatDistanceToNow } from 'date-fns';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DnsIcon from '@mui/icons-material/Dns';
import HubIcon from '@mui/icons-material/Hub';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import NotesIcon from '@mui/icons-material/Notes';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UploadIcon from '@mui/icons-material/Upload';
import GavelIcon from '@mui/icons-material/Gavel';
import MergeIcon from '@mui/icons-material/Merge';

import { AddBlacklistDialog } from '../components/Blacklist/AddBlacklistDialog';

// ============================================================
// Helper Components
// ============================================================

const reasonConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  Threshold: { label: 'Threshold', color: '#ef4444', icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  PrefixAggregation: { label: 'Aggregation', color: '#f59e0b', icon: <MergeIcon sx={{ fontSize: 14 }} /> },
  Manual: { label: 'Manual', color: '#3b82f6', icon: <GavelIcon sx={{ fontSize: 14 }} /> },
  Imported: { label: 'Imported', color: '#8b5cf6', icon: <UploadIcon sx={{ fontSize: 14 }} /> },
};

const ReasonChip: React.FC<{ reason: string }> = ({ reason }) => {
  const cfg = reasonConfig[reason] || { label: reason, color: '#94a3b8', icon: null };
  return (
    <Chip
      icon={cfg.icon as React.ReactElement}
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.6875rem',
        height: 24,
        backgroundColor: alpha(cfg.color, 0.12),
        color: cfg.color,
        '& .MuiChip-icon': { color: cfg.color },
      }}
    />
  );
};

const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}> = ({ icon, label, value, mono, copyable }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (copyable) {
      navigator.clipboard.writeText(copyable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: mono ? '"JetBrains Mono", "Fira Code", monospace' : 'inherit',
              fontWeight: mono ? 500 : 400,
              wordBreak: 'break-all',
            }}
          >
            {value || <span style={{ color: '#94a3b8' }}>-</span>}
          </Typography>
          {copyable && (
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SectionHeader: React.FC<{ title: string; icon: React.ReactNode }> = ({ title, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2.5 }}>
    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
      {title}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: (theme) => alpha(theme.palette.divider, 0.5), ml: 1 }} />
  </Box>
);

// ============================================================
// Main Component
// ============================================================

export const BlacklistPage: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // State
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<BlacklistEntry | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<BlacklistEntry | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [ipCheck, setIpCheck] = useState('');
  const [ipCheckResult, setIpCheckResult] = useState<{ checked: boolean; result?: { isBlacklisted: boolean; entry?: BlacklistEntry } } | null>(null);

  // Debounced search
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
  }, []);

  // Queries
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['blacklist', paginationModel.page + 1, paginationModel.pageSize, debouncedSearch, reasonFilter, statusFilter],
    queryFn: () =>
      blacklistService.getAll({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: debouncedSearch || undefined,
        reason: reasonFilter || undefined,
        isActive: statusFilter === '' ? undefined : statusFilter === 'active',
      }),
    placeholderData: (prev) => prev,
  });

  const { data: stats } = useQuery({
    queryKey: ['blacklist', 'stats'],
    queryFn: blacklistService.getStats,
    refetchInterval: 30000,
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: number) => blacklistService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setDeleteConfirmOpen(false);
      setEntryToDelete(null);
      setSnackbar({ open: true, message: 'Entry deleted successfully', severity: 'success' });
      if (selectedEntry && entryToDelete && selectedEntry.id === entryToDelete.id) {
        setDrawerOpen(false);
        setSelectedEntry(null);
      }
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete entry', severity: 'error' });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      blacklistService.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    },
  });

  const updateNotesMutation = useMutation({
    mutationFn: ({ id, notes, isActive }: { id: number; notes: string; isActive: boolean }) =>
      blacklistService.update(id, { notes, isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setSnackbar({ open: true, message: 'Notes saved', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to save notes', severity: 'error' });
    },
  });

  // Handlers
  const handleRowClick = useCallback((params: GridRowParams<BlacklistEntry>) => {
    setSelectedEntry(params.row);
    setDrawerOpen(true);
  }, []);

  const handleExport = async () => {
    try {
      const blob = await blacklistService.exportList();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blacklist-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Blacklist exported', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  const handleIpCheck = async () => {
    if (!ipCheck.trim()) return;
    try {
      const result = await blacklistService.checkIp(ipCheck.trim());
      setIpCheckResult({ checked: true, result });
    } catch {
      setSnackbar({ open: true, message: 'IP check failed', severity: 'error' });
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setReasonFilter('');
    setStatusFilter('active');
  };

  const hasActiveFilters = debouncedSearch || reasonFilter || statusFilter !== 'active';

  // Columns
  const columns: GridColDef<BlacklistEntry>[] = useMemo(
    () => [
      {
        field: 'ipAddress',
        headerName: 'IP / Prefix',
        flex: 1.2,
        minWidth: 160,
        renderCell: (params) => {
          const value = params.row.ipAddress || params.row.ipPrefix || '';
          const isPrefix = !!params.row.ipPrefix;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isPrefix ? '#f59e0b' : '#3b82f6', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontWeight: 600, fontSize: '0.8125rem' }}>
                {value}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'reason',
        headerName: 'Reason',
        width: 140,
        renderCell: (params) => <ReasonChip reason={params.value || ''} />,
      },
      {
        field: 'hitCount',
        headerName: 'Hits',
        width: 90,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' }}>
            {(params.value as number)?.toLocaleString() || '0'}
          </Typography>
        ),
      },
      {
        field: 'lastActivity',
        headerName: 'Last Activity',
        width: 150,
        renderCell: (params) => {
          if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
          const date = new Date(params.value);
          return (
            <Tooltip title={format(date, 'yyyy-MM-dd HH:mm:ss')}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {formatDistanceToNow(date, { addSuffix: true })}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'addedAt',
        headerName: 'Added',
        width: 150,
        renderCell: (params) => {
          if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
          const date = new Date(params.value);
          return (
            <Tooltip title={format(date, 'yyyy-MM-dd HH:mm:ss')}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {formatDistanceToNow(date, { addSuffix: true })}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.6875rem',
              height: 24,
              ...(params.value
                ? { backgroundColor: alpha('#10b981', 0.12), color: '#10b981' }
                : { backgroundColor: alpha('#94a3b8', 0.12), color: '#94a3b8' }),
            }}
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEntryToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
              sx={{ color: '#ef4444', opacity: 0.4, '&:hover': { opacity: 1, backgroundColor: alpha('#ef4444', 0.08) } }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ====== Header ====== */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Blacklist</Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.totalCount !== undefined ? `${data.totalCount.toLocaleString()} entries` : 'Loading...'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
              <RefreshIcon sx={{ fontSize: 20, animation: isFetching ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Export
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', boxShadow: '0 2px 8px 0 rgba(99, 102, 241, 0.3)' }}
          >
            Add Entry
          </Button>
        </Stack>
      </Box>

      {/* ====== Stats Bar ====== */}
      {stats && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexWrap: 'wrap' }}>
          {[
            { label: 'Active', value: stats.totalActive, color: '#10b981', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
            { label: 'Inactive', value: stats.totalInactive, color: '#94a3b8', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} /> },
            { label: 'IPs', value: stats.ipCount, color: '#3b82f6', icon: <DnsIcon sx={{ fontSize: 16 }} /> },
            { label: 'Prefixes', value: stats.prefixCount, color: '#f59e0b', icon: <HubIcon sx={{ fontSize: 16 }} /> },
            { label: 'Threshold', value: stats.thresholdCount, color: '#ef4444', icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
            { label: 'Manual', value: stats.manualCount, color: '#3b82f6', icon: <GavelIcon sx={{ fontSize: 16 }} /> },
            { label: 'Imported', value: stats.importedCount || 0, color: '#8b5cf6', icon: <UploadIcon sx={{ fontSize: 16 }} /> },
          ].map((stat) => (
            <Paper
              key={stat.label}
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(stat.color, 0.2),
                backgroundColor: alpha(stat.color, 0.04),
              }}
            >
              <Box sx={{ color: stat.color, display: 'flex' }}>{stat.icon}</Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: stat.color, fontSize: '0.875rem' }}>
                {stat.value.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {stat.label}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* ====== Toolbar: Search, Filters, IP Check ====== */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
          <TextField
            placeholder="Search IP or prefix..."
            size="small"
            fullWidth
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleSearchChange('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '& input': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' } } }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Reason</InputLabel>
          <Select value={reasonFilter} label="Reason" onChange={(e) => { setReasonFilter(e.target.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }} sx={{ borderRadius: 2 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Threshold">Threshold</MenuItem>
            <MenuItem value="PrefixAggregation">Aggregation</MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
            <MenuItem value="Imported">Imported</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => { if (v !== null) { setStatusFilter(v); setPaginationModel((prev) => ({ ...prev, page: 0 })); } }}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none', borderRadius: '8px !important' } }}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
          <ToggleButton value="">All</ToggleButton>
        </ToggleButtonGroup>

        {hasActiveFilters && (
          <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters} sx={{ textTransform: 'none', color: 'text.secondary', borderRadius: 2 }}>
            Reset
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* IP Check */}
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleIpCheck(); }} sx={{ display: 'flex', gap: 0.5 }}>
          <TextField
            placeholder="Check IP..."
            size="small"
            value={ipCheck}
            onChange={(e) => { setIpCheck(e.target.value); setIpCheckResult(null); }}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 2, '& input': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' } } }}
          />
          <Button type="submit" variant="outlined" size="small" disabled={!ipCheck.trim()} sx={{ borderRadius: 2, minWidth: 'auto', px: 1.5, textTransform: 'none' }}>
            Check
          </Button>
          {ipCheckResult?.checked && (
            <Chip
              label={ipCheckResult.result?.isBlacklisted ? 'Blocked' : 'Not blocked'}
              size="small"
              sx={{
                fontWeight: 600,
                alignSelf: 'center',
                ...(ipCheckResult.result?.isBlacklisted
                  ? { backgroundColor: alpha('#ef4444', 0.12), color: '#ef4444' }
                  : { backgroundColor: alpha('#10b981', 0.12), color: '#10b981' }),
              }}
            />
          )}
        </Box>
      </Paper>

      {/* ====== Data Grid ====== */}
      <Paper sx={{ flex: 1, minHeight: 0, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          loading={isLoading}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          rowHeight={48}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: alpha(theme.palette.background.default, 0.6), borderBottom: '1px solid', borderColor: 'divider' },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' },
            '& .MuiDataGrid-row': { cursor: 'pointer', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } },
            '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5) },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
        />
      </Paper>

      {/* ====== Detail Drawer ====== */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}
      >
        {selectedEntry && (
          <BlacklistDetailDrawer
            entry={selectedEntry}
            onClose={() => setDrawerOpen(false)}
            onToggleActive={(isActive) => {
              toggleMutation.mutate({ id: selectedEntry.id, isActive });
              setSelectedEntry({ ...selectedEntry, isActive });
            }}
            onSaveNotes={(notes) => {
              updateNotesMutation.mutate({ id: selectedEntry.id, notes, isActive: selectedEntry.isActive });
              setSelectedEntry({ ...selectedEntry, notes });
            }}
            onDelete={() => {
              setEntryToDelete(selectedEntry);
              setDeleteConfirmOpen(true);
            }}
          />
        )}
      </Drawer>

      {/* ====== Add Dialog ====== */}
      <AddBlacklistDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />

      {/* ====== Delete Confirm ====== */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 380 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Delete Entry</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            This will permanently remove this entry from the blacklist.
          </Alert>
          <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace', backgroundColor: alpha('#000', 0.04), p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            {entryToDelete?.ipAddress || entryToDelete?.ipPrefix}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={() => entryToDelete && deleteMutation.mutate(entryToDelete.id)}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====== Snackbar ====== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================================
// Detail Drawer
// ============================================================

const BlacklistDetailDrawer: React.FC<{
  entry: BlacklistEntry;
  onClose: () => void;
  onToggleActive: (isActive: boolean) => void;
  onSaveNotes: (notes: string) => void;
  onDelete: () => void;
}> = ({ entry, onClose, onToggleActive, onSaveNotes, onDelete }) => {
  const [notes, setNotes] = useState(entry.notes || '');
  const [notesEdited, setNotesEdited] = useState(false);

  React.useEffect(() => {
    setNotes(entry.notes || '');
    setNotesEdited(false);
  }, [entry.id, entry.notes]);

  const identifier = entry.ipAddress || entry.ipPrefix || 'Unknown';
  const isPrefix = !!entry.ipPrefix;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2.5, pb: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontWeight: 700, fontSize: '1rem', wordBreak: 'break-all', mb: 1 }}>
            {identifier}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            <ReasonChip reason={entry.reason} />
            <Chip
              label={isPrefix ? 'Prefix' : 'IP'}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.6875rem', height: 24 }}
            />
            <Chip
              label={entry.isActive ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.6875rem',
                height: 24,
                ...(entry.isActive
                  ? { backgroundColor: alpha('#10b981', 0.12), color: '#10b981' }
                  : { backgroundColor: alpha('#94a3b8', 0.12), color: '#94a3b8' }),
              }}
            />
          </Stack>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 2 }}>
        {/* Toggle Active */}
        <Box sx={{ py: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={entry.isActive}
                onChange={(e) => onToggleActive(e.target.checked)}
                color={entry.isActive ? 'success' : 'default'}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {entry.isActive ? 'Active - blocking traffic' : 'Inactive - not blocking'}
              </Typography>
            }
          />
        </Box>

        <Divider />

        {/* Details */}
        <SectionHeader title="Details" icon={<FingerprintIcon sx={{ fontSize: 18 }} />} />
        <DetailRow icon={<BlockIcon sx={{ fontSize: 18 }} />} label="Entry" value={identifier} mono copyable={identifier} />
        <DetailRow icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} label="Hit Count" value={entry.hitCount?.toLocaleString() || '0'} />
        <DetailRow icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Added By" value={entry.addedBy || 'System'} />

        {/* Timestamps */}
        <SectionHeader title="Timestamps" icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} />
        <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Added" value={entry.addedAt ? format(new Date(entry.addedAt), 'yyyy-MM-dd HH:mm:ss') : '-'} />
        <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="First Seen" value={entry.firstSeen ? format(new Date(entry.firstSeen), 'yyyy-MM-dd HH:mm:ss') : '-'} />
        <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Last Seen" value={entry.lastSeen ? format(new Date(entry.lastSeen), 'yyyy-MM-dd HH:mm:ss') : '-'} />
        <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Last Activity" value={entry.lastActivity ? format(new Date(entry.lastActivity), 'yyyy-MM-dd HH:mm:ss') : '-'} />
        {entry.autoRemoveAfterDays && (
          <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Auto-remove After" value={`${entry.autoRemoveAfterDays} days`} />
        )}

        {/* Notes */}
        <SectionHeader title="Notes" icon={<NotesIcon sx={{ fontSize: 18 }} />} />
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Add notes..."
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setNotesEdited(true); }}
          size="small"
          sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8125rem' } }}
        />
        {notesEdited && (
          <Button
            size="small"
            variant="contained"
            onClick={() => { onSaveNotes(notes); setNotesEdited(false); }}
            sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }}
          >
            Save Notes
          </Button>
        )}
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Delete Entry
        </Button>
      </Box>
    </Box>
  );
};
